import { useToast } from '@/composables/useToast'; // ✅ useToast 사용
import {useI18n} from 'vue-i18n';

export function useHandleCode() {
	// error 객체 가져오기
	const { t, tm } = useI18n();
	const { showToast } = useToast(); // ✅ useToast 상태 사용

	/**
	 * 코드 처리 함수
	 * @param mainCodeGroup	메인 코드 그룹
	 * @param subCodeGroup	코드 그룹
	 * @param code			코드
	 */
	const handleCode = (mainCodeGroup: string, subCodeGroup: string, code: number | string) => {
		// `tm`을 호출하여 해당 코드 그룹 객체 반환
		const codeGroups = tm(`${mainCodeGroup}.${subCodeGroup}`);

		// 객체가 존재하는지 확인
		if (!codeGroups || typeof codeGroups !== 'object') {
			console.error(`Invalid code group: ${mainCodeGroup}.${subCodeGroup}`);
			return '-';
		}

		// `code`가 존재하는지 확인 후 반환
		if (!(code in codeGroups)) {
			console.warn(`Code ${code} not found in ${mainCodeGroup}.${subCodeGroup}`);
			return '-';
		}

		// 코드 메시지를 반환
		return t(`${mainCodeGroup}.${subCodeGroup}.${code}`);
	}

	/**
	 * 코드 처리 함수
	 * @param mainCodeGroup	메인 코드 그룹
	 * @param code			코드
	 */
	const handleCodeDepth = (mainCodeGroup: string, code: number | string) => {
		// `tm`을 호출하여 해당 코드 그룹 객체 반환
		const codeGroups = tm(`${mainCodeGroup}`);

		// 객체가 존재하는지 확인
		if (!codeGroups || typeof codeGroups !== 'object') {
			console.error(`Invalid code group: ${mainCodeGroup}`);
			return '-';
		}

		// `code`가 존재하는지 확인 후 반환
		if (!(code in codeGroups)) {
			console.warn(`Code ${code} not found in ${mainCodeGroup}`);
			return t(`${mainCodeGroup}.${code}`);
		}

		// 코드 메시지를 반환
		return t(`${mainCodeGroup}.${code}`);
	}

	/**
	 * 상수 코드 배열 처리 함수
	 * @param mainCodeGroup	메인 코드 그룹
	 * @param subCodeGroup	코드 그룹
	 */
	const handleConstantsCodes = (mainCodeGroup: string, subCodeGroup: string) => {
		const codeGroups = tm(`${mainCodeGroup}.${subCodeGroup}`);
		return Array.isArray(codeGroups) ? codeGroups : [];
	};

	/**
	 * 에러 코드 처리 함수
	 * @param errorCode	에러 코드
	 */
	const handleErrorCode = (errorCode: string) => {
		console.log('[useHandleCode.ts] handleErrorCode 함수 시작');
		// error 객체 가져오기
		const errorCodes = tm('error'); // `tm`을 사용하여 전체 객체 반환
		// `ko.json`에 정의된 에러 코드인지 확인 후 존재하지 않으면 기본 코드 'E50001' 사용
		const validErrorCode = errorCode in errorCodes ? errorCode : 'E50001';
		// 에러 메시지를 i18n에서 가져오기
		const errorMessage = t(`error.${validErrorCode}`);
		// 특정 에러코드(`E99998`)인 경우 리디렉션 여부 설정
		const shouldRedirect = validErrorCode === 'E99998';
		showToast(errorMessage, 'error', 2000, shouldRedirect); // ✅ `useToast`의 showToast 사용
		console.log('[useHandleCode.ts] handleErrorCode 함수 종료');
	};

	/**
	 * 성공 코드 처리 함수
	 * @param successCode	에러 코드
	 */
	const handleSuccessCode = (successCode: string) => {
		// success 객체 가져오기
		const successCodes = tm('success'); // `tm`을 사용하여 전체 객체 반환
		// `ko.json`에 정의된 에러 코드인지 확인 후 존재하지 않으면 기본 코드 'E50001' 사용
		const validSuccessCode = successCode in successCodes ? successCode : 'S00000';
		// 에러 메시지를 i18n에서 가져오기
		const successMessage = t(`success.${validSuccessCode}`);
		showToast(successMessage, 'success', 2000); // ✅ `useToast`의 showToast 사용
	};

	const handleVerificationMessage = (messageKey: string, type: 'success' | 'error' = 'error') => {
		const message = t(`messages.verification.${messageKey}`);
		showToast(message, type, 2000);
	}

	/**
	 * 알람 토스트 메시지를 보여주는 함수 (다중 토스트 지원)
	 * @param faultHistory FaultHistoryData 객체
	 */
	const showAlarmToast = (faultHistory: any) => {
		const { addToast } = useToast(); // addToast 함수 가져오기
		
		// faultDescription에 언어팩 적용
		const translatedFaultDescription = handleCodeDepth('faultItem', faultHistory.faultItem);
		
		// 토스트 메시지 구성: facilityName + terminalName + deviceId + faultDescription
		const toastMessage = `${faultHistory.facility.facilityName} ${faultHistory.terminal.terminalName} ${translatedFaultDescription}`;
		
		// 다중 알람 토스트를 warning 색상으로 5초간 표시
		addToast(toastMessage, 'warning', 5000);
	}

	return { handleCode, handleCodeDepth, handleConstantsCodes, handleErrorCode, handleSuccessCode, handleVerificationMessage, showAlarmToast };
}